import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createOperationalControlsRegisterItemApi,
  decideEmsApprovalApi,
  deleteOperationalControlsRegisterItemApi,
  downloadOperationalControlsRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchOperationalControlsRegisterApi,
  requestEmsApprovalApi,
  updateOperationalControlsRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "operational_controls";

const EMPTY_FORM = {
  iso_clause: "",
  title: "",
  area: "",
  control_type: "",
  description: "",
  monitoring_method: "",
  frequency: "",
  owner: "",
  status: "planned",
  next_check_date: "",
  evidence: "",
  notes: "",
};

export default function OperationalControls() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchOperationalControlsRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load operational controls register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("8");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "8" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.title || "").trim()) errs.push("Title is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    const st = String(form.status || "").trim().toLowerCase();
    if (st === "effective" || st === "closed") {
      if (!String(form.area || "").trim()) errs.push("Area is required before setting this completion status.");
      if (!String(form.control_type || "").trim()) errs.push("Control type is required before setting this completion status.");
      if (!String(form.description || "").trim()) errs.push("Description is required before setting this completion status.");
      if (!String(form.monitoring_method || "").trim()) errs.push("Monitoring method is required before setting this completion status.");
      if (!String(form.frequency || "").trim()) errs.push("Frequency is required before setting this completion status.");
      if (!String(form.owner || "").trim()) errs.push("Owner is required before setting this completion status.");
      if (!String(form.next_check_date || "").trim()) errs.push("Next check date is required before setting this completion status.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting this completion status.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("operational_controls");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["effective", "closed"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      title: item.title || "",
      area: item.area || "",
      control_type: item.control_type || "",
      description: item.description || "",
      monitoring_method: item.monitoring_method || "",
      frequency: item.frequency || "",
      owner: item.owner || "",
      status: item.status || "planned",
      next_check_date: item.next_check_date || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateOperationalControlsRegisterItemApi(editingId, form);
        toast.success("Operational control updated");
      } else {
        await createOperationalControlsRegisterItemApi(form);
        toast.success("Operational control created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save operational control");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this operational control entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteOperationalControlsRegisterItemApi(id);
      toast.success("Operational control deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete operational control");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadOperationalControlsRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "operational_controls_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Operational controls register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export operational controls register");
    } finally {
      setExporting(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const set = new Set((items || []).map((it) => String(it.owner || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.next_check_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.next_check_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterOwner, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "planned_only") {
      setFilterStatus("planned");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "ineffective_only") {
      setFilterStatus("ineffective");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    setFilterStatus("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 operation</p>
          <h1>Operational Controls</h1>
          <p className="subtitle">Record the practical procedures and checks used to manage significant environmental aspects, risks and compliance duties in day-to-day operations.</p>
        </div>
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to complete a control</p>
          <h3>Define what must happen, how it is checked, and who is responsible</h3>
          <p>Use this register for controls that prevent or reduce environmental impacts, manage risks, or meet compliance obligations during normal operations.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Define</strong><span>Describe the control, the area or process it covers, and the expected way of working.</span></div>
          <div><strong>Monitor</strong><span>State how compliance or effectiveness will be checked and how often.</span></div>
          <div><strong>Review</strong><span>Assign an owner, next check date and evidence that the control is operating.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header">
          <div><p className="guided-register-eyebrow">Your operational control</p><h3>{editingId ? "Edit Control" : "Add Control"}</h3></div>
          <span className="kpi-sub">Record a practical control that people can follow and that can be checked for effectiveness.</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Waste segregation procedure" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving} />
              <small>Use a short name that identifies the control people must follow.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="planned">planned</option>
                <option value="implemented">implemented</option>
                <option value="effective">effective</option>
                <option value="ineffective">ineffective</option>
                <option value="closed">closed</option>
              </select>
              <small>Mark effective only after monitoring shows the control is working as intended.</small>
            </label>
            <label className="settings-field">
              <span>Area / process</span>
              <input placeholder="e.g. Warehouse waste handling" value={form.area} onChange={(e) => setField("area", e.target.value)} disabled={saving} />
              <small>State the location, activity, equipment or process where the control applies.</small>
            </label>
            <label className="settings-field">
              <span>Control type</span>
              <input placeholder="e.g. Procedure and training" value={form.control_type} onChange={(e) => setField("control_type", e.target.value)} disabled={saving} />
              <small>Describe the kind of control, for example procedure, inspection, engineering control or training.</small>
            </label>
            <label className="settings-field">
              <span>Monitoring method</span>
              <input placeholder="e.g. Monthly waste-area inspection" value={form.monitoring_method} onChange={(e) => setField("monitoring_method", e.target.value)} disabled={saving} />
              <small>State how you will check the control is in place and working.</small>
            </label>
            <label className="settings-field">
              <span>Frequency</span>
              <input placeholder="e.g. Monthly" value={form.frequency} onChange={(e) => setField("frequency", e.target.value)} disabled={saving} />
              <small>State how often the control or monitoring activity must be completed.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Warehouse Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving} />
              <small>Name the person or role accountable for operating and checking the control.</small>
            </label>
            <label className="settings-field">
              <span>Next check date</span>
              <input type="date" value={form.next_check_date} onChange={(e) => setField("next_check_date", e.target.value)} disabled={saving} />
              <small>Set the next planned control check or effectiveness review.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Description</span>
              <input placeholder="Describe the required steps, responsibilities and conditions for this control." value={form.description} onChange={(e) => setField("description", e.target.value)} disabled={saving} />
              <small>Write enough detail for a colleague or auditor to understand what the control requires.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence / link"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link procedures, inspection records, training evidence, checklists, monitoring logs or other records showing the control is operating."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record control changes, monitoring findings, actions or review decisions." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update control" : "Add control"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar" style={{ gridTemplateColumns: "180px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="planned">planned</option>
            <option value="implemented">implemented</option>
            <option value="effective">effective</option>
            <option value="ineffective">ineffective</option>
            <option value="closed">closed</option>
          </select>
              <small>Mark effective only after monitoring shows the control is working as intended.</small>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue check only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("planned_only")}>Only planned</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("ineffective_only")}>Only ineffective</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Check due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Title</th>
              <th>Area</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Owner</th>
              <th>Next check</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={12} className="table-empty">No operational controls yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td>{it.area || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.owner || "-"}</td>
                  <td>{it.next_check_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



